import { useEffect, useRef, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  KeyRound, Eye, EyeOff, Copy, RotateCcw, Ban, History, ShieldAlert,
  Search, ChevronLeft, ChevronRight, Lock, Loader2, ArrowLeft, ShieldCheck, CheckCircle2,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const REVEAL_SECONDS = 20;

const STATUS_META = {
  active: { label: "Active", cls: "text-emerald-600 border-emerald-500/40" },
  expired: { label: "Expired", cls: "text-amber-600 border-amber-500/40" },
  used: { label: "Used", cls: "text-blue-600 border-blue-500/40" },
  revoked: { label: "Revoked", cls: "text-rose-600 border-rose-500/40" },
  none: { label: "No temp password", cls: "text-muted-foreground border-border" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

async function clip(text, label = "Copied") {
  try { await navigator.clipboard.writeText(text); toast.success(label); }
  catch { toast.message("Copy manually", { description: text }); }
}

export default function TempPasswords() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // step-up reauth
  const reauthRef = useRef({ token: null, exp: 0 });
  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthPw, setReauthPw] = useState("");
  const [reauthBusy, setReauthBusy] = useState(false);
  const pendingRef = useRef(null); // function to run after reauth

  // reveal state: { [userId]: {password, timer} }
  const [revealed, setRevealed] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type:'reset'|'revoke', row}
  const [auditFor, setAuditFor] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/admin/temp-passwords`, { params: { q, status, page, page_size: pageSize } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [q, status, page]);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const reauthValid = () => reauthRef.current.token && Date.now() < reauthRef.current.exp;

  // Ensure we have a valid reauth token; if not, open the modal and queue the action.
  const withReauth = (fn) => {
    if (reauthValid()) { fn(reauthRef.current.token); return; }
    pendingRef.current = fn;
    setReauthPw("");
    setReauthOpen(true);
  };

  const submitReauth = async (e) => {
    e.preventDefault();
    setReauthBusy(true);
    try {
      const { data: res } = await api.post("/admin/reauth", { password: reauthPw });
      reauthRef.current = { token: res.reauth_token, exp: Date.now() + Math.max(30, res.expires_in - 5) * 1000 };
      setReauthOpen(false);
      setReauthPw("");
      const fn = pendingRef.current;
      pendingRef.current = null;
      if (fn) fn(reauthRef.current.token);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setReauthBusy(false);
    }
  };

  const autoHide = (id) => {
    setTimeout(() => {
      setRevealed((r) => { const n = { ...r }; delete n[id]; return n; });
    }, REVEAL_SECONDS * 1000);
  };

  const doReveal = (row) => withReauth(async (token) => {
    setBusyId(row.id);
    try {
      const { data: res } = await api.post(`/admin/temp-passwords/${row.id}/reveal`, { reauth_token: token });
      setRevealed((r) => ({ ...r, [row.id]: res.temp_password }));
      autoHide(row.id);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusyId(null); }
  });

  const doCopy = (row) => {
    const pw = revealed[row.id];
    if (!pw) return;
    clip(pw, "Temporary password copied");
    // audit the copy (best-effort, requires reauth)
    if (reauthValid()) api.post(`/admin/temp-passwords/${row.id}/copy-log`, { reauth_token: reauthRef.current.token }).catch(() => {});
  };

  const doGenerate = (row) => withReauth(async (token) => {
    setBusyId(row.id);
    try {
      const { data: res } = await api.post(`/admin/temp-passwords/${row.id}/generate`, { reauth_token: token });
      setRevealed((r) => ({ ...r, [row.id]: res.temp_password }));
      autoHide(row.id);
      toast.success(res.email_sent ? "New temporary password generated & emailed" : "New temporary password generated");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusyId(null); setConfirm(null); }
  });

  const doRevoke = (row) => withReauth(async (token) => {
    setBusyId(row.id);
    try {
      await api.post(`/admin/temp-passwords/${row.id}/revoke`, { reauth_token: token });
      setRevealed((r) => { const n = { ...r }; delete n[row.id]; return n; });
      toast.success("Temporary password revoked");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusyId(null); setConfirm(null); }
  });

  const openAudit = async (row) => {
    setAuditFor(row);
    setAuditLogs([]);
    try {
      const { data: res } = await api.get(`/admin/temp-passwords/${row.id}/audit`);
      setAuditLogs(res.audit || []);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const records = data?.records || [];
  const counts = data?.counts || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="temp-passwords-page">
      <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="tp-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Admin Portal
      </button>

      <div className="flex items-center gap-3">
        <KeyRound className="h-6 w-6 text-primary" />
        <h1 className="font-display font-bold text-3xl tracking-tight">Temporary Password Management</h1>
      </div>
      <p className="text-muted-foreground mt-1">
        Reveal, reset, or revoke active temporary passwords. Permanent passwords are never stored or shown.
      </p>

      {/* Confidential warning */}
      <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm" data-testid="tp-warning">
        <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <span>
          Temporary passwords are confidential. Revealing requires re-authentication, is logged, and is hidden again after {REVEAL_SECONDS}s.
          Share credentials only over a secure channel. A temp password can no longer be revealed once the user has used it.
        </span>
      </div>

      {/* Controls */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="tp-search"
            className="pl-9"
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[190px]" data-testid="tp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active {counts.active ? `(${counts.active})` : ""}</SelectItem>
            <SelectItem value="expired">Expired {counts.expired ? `(${counts.expired})` : ""}</SelectItem>
            <SelectItem value="used">Used {counts.used ? `(${counts.used})` : ""}</SelectItem>
            <SelectItem value="revoked">Revoked {counts.revoked ? `(${counts.revoked})` : ""}</SelectItem>
            <SelectItem value="none">No temp password {counts.none ? `(${counts.none})` : ""}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-card mt-4 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Temp status</TableHead>
                <TableHead>Temporary password</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Created by</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
              ) : records.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No users match this filter.</TableCell></TableRow>
              ) : records.map((row) => {
                const meta = STATUS_META[row.temp_status] || STATUS_META.none;
                const shown = revealed[row.id];
                return (
                  <TableRow key={row.id} data-testid={`tp-row-${row.id}`}>
                    <TableCell>
                      <div className="font-medium">{row.name}</div>
                      <div className="text-xs text-muted-foreground">{row.email}</div>
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{row.role}</Badge></TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${meta.cls}`} data-testid={`tp-status-${row.id}`}>{meta.label}</Badge>
                    </TableCell>
                    <TableCell>
                      {shown ? (
                        <span className="font-mono text-sm bg-secondary px-2 py-1 rounded" data-testid={`tp-revealed-${row.id}`}>{shown}</span>
                      ) : row.revealable ? (
                        <span className="font-mono text-muted-foreground select-none">{row.masked}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">not retrievable</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmtDate(row.created_at)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{row.temp_status === "active" ? fmtDate(row.expires_at) : "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{row.created_by_email || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {row.revealable && (shown ? (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Copy" onClick={() => doCopy(row)} data-testid={`tp-copy-${row.id}`}><Copy className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Hide" onClick={() => setRevealed((r) => { const n = { ...r }; delete n[row.id]; return n; })} data-testid={`tp-hide-${row.id}`}><EyeOff className="h-4 w-4" /></Button>
                          </>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Reveal" disabled={busyId === row.id} onClick={() => doReveal(row)} data-testid={`tp-reveal-${row.id}`}>
                            {busyId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        ))}
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Generate new temporary password" disabled={busyId === row.id} onClick={() => setConfirm({ type: "reset", row })} data-testid={`tp-reset-${row.id}`}><RotateCcw className="h-4 w-4" /></Button>
                        {row.temp_status === "active" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-600" title="Revoke" disabled={busyId === row.id} onClick={() => setConfirm({ type: "revoke", row })} data-testid={`tp-revoke-${row.id}`}><Ban className="h-4 w-4" /></Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Audit history" onClick={() => openAudit(row)} data-testid={`tp-audit-${row.id}`}><History className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
        <span>{total} user{total === 1 ? "" : "s"}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="tp-prev-page"><ChevronLeft className="h-4 w-4" /> Prev</Button>
          <span className="font-mono">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" className="gap-1" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="tp-next-page">Next <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Reauth modal */}
      <Dialog open={reauthOpen} onOpenChange={(o) => { if (!o) { setReauthOpen(false); pendingRef.current = null; } }}>
        <DialogContent data-testid="tp-reauth-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> Confirm it's you</DialogTitle>
            <DialogDescription>For security, re-enter your admin password to manage temporary passwords. This authorization lasts a few minutes.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReauth} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reauth-pw">Your password</Label>
              <Input id="reauth-pw" type="password" autoFocus value={reauthPw} onChange={(e) => setReauthPw(e.target.value)} required data-testid="tp-reauth-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setReauthOpen(false); pendingRef.current = null; }}>Cancel</Button>
              <Button type="submit" disabled={reauthBusy} data-testid="tp-reauth-submit">{reauthBusy ? "Verifying…" : "Confirm"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm reset/revoke */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="tp-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "reset" ? `Generate a new temporary password for ${confirm?.row.name}?` : `Revoke ${confirm?.row.name}'s temporary password?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "reset"
                ? "This replaces any existing temporary password with a new one. The user must change it on first sign-in. If email is configured, it is sent automatically."
                : "This immediately invalidates the temporary password so it can no longer be used or revealed. You'll need to generate a new one to restore the user's access."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="tp-confirm-action"
              onClick={() => { if (confirm?.type === "reset") doGenerate(confirm.row); else doRevoke(confirm.row); }}
            >
              {confirm?.type === "reset" ? "Generate new" : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit history */}
      <Dialog open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)}>
        <DialogContent data-testid="tp-audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Audit history</DialogTitle>
            <DialogDescription>{auditFor?.name} · {auditFor?.email}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {auditLogs.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No audit records yet.</div>
            ) : auditLogs.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm" data-testid={`tp-audit-row-${a.id}`}>
                <div>
                  <div className="font-medium capitalize flex items-center gap-2">
                    {a.result === "success" ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />}
                    {a.action} <span className="text-xs font-normal text-muted-foreground">· {a.result}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">by {a.admin_email}{a.ip ? ` · ${a.ip}` : ""}</div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0">{fmtDate(a.timestamp)}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
